import React, { useEffect, useState } from "react";
import OutlookAppearancePane from "@/pages/OutlookAppearancePane";
import OutlookTaskPane from "@/pages/OutlookTaskPane";
import { isOutlookAppointment } from "@/lib/outlookAppointmentClient";

const OFFICE_JS_URL = "https://appsforoffice.microsoft.com/lib/1/hosted/office.js";
let officeScriptPromise;

function requestedPreviewSurface() {
  const value = new URLSearchParams(window.location.search).get("mode");
  return value === "appointment" ? "appointment" : "message";
}

function loadOfficeJs() {
  if (window.Office) return Promise.resolve();
  if (officeScriptPromise) return officeScriptPromise;

  officeScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${OFFICE_JS_URL}"]`);
    if (existing) {
      existing.addEventListener("load", resolve, { once: true });
      existing.addEventListener("error", reject, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = OFFICE_JS_URL;
    script.type = "text/javascript";
    script.addEventListener("load", resolve, { once: true });
    script.addEventListener("error", reject, { once: true });
    document.head.appendChild(script);
  });

  return officeScriptPromise;
}

function OutlookAddinContent() {
  const [surface, setSurface] = useState("");

  useEffect(() => {
    let active = true;
    const previewSurface = requestedPreviewSurface();
    let fallbackTimer;

    loadOfficeJs()
      .then(() => {
        fallbackTimer = window.setTimeout(() => {
          if (active) setSurface(previewSurface);
        }, 2000);

        window.Office.onReady((info) => {
          if (!active) return;
          window.clearTimeout(fallbackTimer);
          if (info.host !== window.Office.HostType.Outlook) {
            setSurface(previewSurface);
            return;
          }

          const item = window.Office.context.mailbox.item;
          setSurface(isOutlookAppointment(item) ? "appointment" : "message");
        });
      })
      .catch(() => {
        if (active) setSurface(previewSurface);
      });

    return () => {
      active = false;
      window.clearTimeout(fallbackTimer);
    };
  }, []);

  if (!surface) {
    return (
      <main className="grid min-h-screen place-items-center bg-slate-50 text-sm font-semibold text-slate-500">
        Loading Outlook...
      </main>
    );
  }

  return surface === "appointment" ? <OutlookAppearancePane /> : <OutlookTaskPane />;
}

class OutlookPaneErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("CLS Connect task pane failed to render", error, info);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <main className="grid min-h-screen place-items-center bg-slate-50 p-5 text-slate-900">
        <section className="w-full max-w-sm rounded-2xl border border-red-200 bg-white p-5 shadow-sm">
          <h1 className="text-base font-bold text-red-700">CLS Connect could not start</h1>
          <p className="mt-2 text-sm text-slate-600">
            Close this pane and open it again. If the problem continues, send Complete Law Support the message below.
          </p>
          <pre className="mt-3 whitespace-pre-wrap rounded-xl bg-red-50 p-3 text-xs text-red-800">
            {this.state.error?.message || "Unknown task pane error"}
          </pre>
          <button
            type="button"
            className="mt-4 w-full rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white"
            onClick={() => window.location.reload()}
          >
            Reload CLS Connect
          </button>
        </section>
      </main>
    );
  }
}

export default function OutlookAddin() {
  return (
    <OutlookPaneErrorBoundary>
      <OutlookAddinContent />
    </OutlookPaneErrorBoundary>
  );
}
